import React, { useState, useEffect, useMemo } from 'react';
import OpenRouterProvider from '../../../services/ai/providers/OpenRouterProvider';

/**
 * Model Selector Component with Search
 * Fetches and displays models from OpenRouter with search/filter capabilities
 */
const ModelSelector = ({ value, onChange, apiKey, providerType = 'openrouter' }) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFree, setFilterFree] = useState(false);
  const [sortBy, setSortBy] = useState('name'); // name, cost, context

  useEffect(() => {
    if (providerType === 'openrouter' && apiKey) {
      fetchModels();
    }
  }, [providerType, apiKey]);

  const fetchModels = async () => {
    setLoading(true);
    setError(null);

    try {
      const fetchedModels = await OpenRouterProvider.fetchModels(apiKey);
      setModels(fetchedModels);
    } catch (err) {
      setError(err.message || 'Failed to fetch models');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort models
  const filteredModels = useMemo(() => {
    let filtered = models;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(model =>
        model.id.toLowerCase().includes(term) ||
        model.name.toLowerCase().includes(term) ||
        model.description?.toLowerCase().includes(term)
      );
    }

    // Free models filter
    if (filterFree) {
      filtered = filtered.filter(model =>
        model.pricing.prompt === 0 && model.pricing.completion === 0
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'cost':
          return (a.pricing.prompt + a.pricing.completion) - (b.pricing.prompt + b.pricing.completion);
        case 'context':
          return (b.context_length || 0) - (a.context_length || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [models, searchTerm, filterFree, sortBy]);

  const formatCost = (cost) => {
    if (cost === 0) return 'Free';
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    if (cost < 1) return `$${cost.toFixed(3)}`;
    return `$${cost.toFixed(2)}`;
  };

  if (providerType !== 'openrouter') {
    // Manual input for non-OpenRouter providers
    return (
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Model Name *
        </label>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm placeholder-gray-500"
          placeholder="gpt-4o-mini, gemini-2.5-flash, claude-3-5-sonnet, etc."
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Enter the exact model identifier
        </p>
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="text-yellow-400 text-sm">
            Add OpenRouter API key to your .env file to fetch available models
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-300">
          Select Model {loading && <span className="text-gray-400">(Loading...)</span>}
        </label>

        {/* Search Input */}
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search models (e.g., gpt-4, claude, llama)..."
            className="w-full pl-10 pr-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
          />
          <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={filterFree}
              onChange={(e) => setFilterFree(e.target.checked)}
              className="w-4 h-4 text-blue-500 border-gray-600 rounded focus:ring-blue-500 bg-gray-700"
            />
            <span className="ml-2 text-sm text-gray-300">Free models only</span>
          </label>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-600 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="name">Sort by Name</option>
            <option value="cost">Sort by Cost</option>
            <option value="context">Sort by Context Length</option>
          </select>

          <button
            onClick={fetchModels}
            className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
            disabled={loading}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Results count */}
        <p className="text-xs text-gray-400">
          Showing {filteredModels.length} of {models.length} models
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-3">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Model List */}
      <div className="border border-gray-600 rounded-lg max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : filteredModels.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            {searchTerm ? 'No models found matching your search' : 'No models available'}
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {filteredModels.map((model) => (
              <label
                key={model.id}
                className={`flex items-start p-3 cursor-pointer hover:bg-gray-700/50 ${value === model.id ? 'bg-blue-900/20' : ''
                  }`}
              >
                <input
                  type="radio"
                  name="model"
                  value={model.id}
                  checked={value === model.id}
                  onChange={() => onChange(model.id, model.pricing)}
                  className="mt-1 w-4 h-4 text-blue-500 border-gray-600 focus:ring-blue-500 bg-gray-700"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{model.name}</p>
                      <p className="text-xs text-gray-400 font-mono mt-0.5">{model.id}</p>
                      {model.description && (
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{model.description}</p>
                      )}
                    </div>
                    <div className="ml-4 flex flex-col items-end">
                      <span className={`text-xs font-medium ${model.pricing.prompt === 0 && model.pricing.completion === 0
                        ? 'text-green-400'
                        : 'text-white'
                        }`}>
                        {model.pricing.prompt === 0 && model.pricing.completion === 0 ? (
                          'Free'
                        ) : (
                          <>
                            {formatCost(model.pricing.prompt)} / {formatCost(model.pricing.completion)}
                          </>
                        )}
                      </span>
                      {model.context_length && (
                        <span className="text-xs text-gray-400 mt-1">
                          {(model.context_length / 1000).toFixed(0)}k ctx
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    {model.architecture?.modality === 'text+image' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-900/20 text-purple-400 border border-purple-800">
                        Vision
                      </span>
                    )}
                    {model.top_provider && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-700 text-gray-300 border border-gray-600">
                        {model.top_provider.name}
                      </span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Selected Model Info */}
      {value && (
        <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-3">
          <p className="text-sm text-blue-400">
            <strong>Selected:</strong> {models.find(m => m.id === value)?.name || value}
          </p>
        </div>
      )}
    </div>
  );
};

export default ModelSelector;
